import axios from "axios";
import {transformFuelPercentageToRwFuel} from "./utils";

const keylessAxios = axios.create({
  baseURL: process.env.REACT_APP_WEB_API_URL,
});

const keylessIdHeaders = (token) => ({
  headers: {
    "x-keyless-id": token,
    "Accept-Language": JSON.parse(localStorage.getItem('language'))?.lang || 'pt'
  },
});

export async function getKeylessSession(token) {
  return (await keylessAxios.get("/keyless", keylessIdHeaders(token))).data;
}

export async function getReturnKeylessSession(token) {
  return (await keylessAxios.get("/keyless-return", keylessIdHeaders(token))).data;
}

export async function validateKeylessLocation(token, location) {
  return (
    await keylessAxios.post(
      "/keyless/validate-location",
      location,
      keylessIdHeaders(token)
    )
  ).data;
}

export async function validateKeylessDropOffLocation(token) {
  return (
    await keylessAxios.post(
      "/keyless/validate-vehicle-dropoff",
      undefined,
      keylessIdHeaders(token)
    )
  ).data;
}

export async function sendKeylessPin(token) {
  return (
    await keylessAxios.post("/keyless/pin", undefined, keylessIdHeaders(token))
  ).data;
}

export async function validateKeylessPin(token, pin) {
  return (
    await keylessAxios.post(
      "/keyless/pin-validate",
      {pin},
      keylessIdHeaders(token)
    )
  ).data;
}

export async function validateKeylessRA(token) {
  return (
    await keylessAxios.post(
      "/keyless/validate-rental-agreement",
      undefined,
      keylessIdHeaders(token)
    )
  ).data;
}

export async function keylessSignRA(token, signature) {
  return (
    await keylessAxios.post(
      "/keyless/sign-rental-agreement",
      {signature},
      keylessIdHeaders(token)
    )
  ).data;
}

export async function getKeylessSessionRAReview(token) {
  return (
    await keylessAxios.get("/keyless/rental-agreement", keylessIdHeaders(token))
  ).data;
}

export async function validateKeylessVehicleQRCode(token, qrCode) {
  return (
    await keylessAxios.post(
      "/keyless/validate-vehicle",
      {qrCode},
      keylessIdHeaders(token)
    )
  ).data;
}

export async function validateReturnKeylessVehicleQRCode(token, qrCode) {
  return (
    await keylessAxios.post(
      "/keyless/validate-vehicle-return",
      {qrCode},
      keylessIdHeaders(token)
    )
  ).data;
}

export async function keylessOpenDoors(token) {
  return (
    await keylessAxios.post(
      "/keyless/open-doors",
      undefined,
      keylessIdHeaders(token)
    )
  ).data;
}

// Leituras de referência do veículo: nível de combustível (convertido para a escala
// 0–8 da RentWay) e kms. Ambos vêm da telemática, com fallback à RentWay.
export async function keylessCheckFuelLevelAndKms(token) {
  const data = (
    await keylessAxios.get(
      "/keyless/vehicle-readings",
      keylessIdHeaders(token)
    )
  ).data;

  return {
    fuelLevel: transformFuelPercentageToRwFuel(data.percentage),
    kms: data.kms,
  };
}

export async function finishKeylessSession(token) {
  return (
    await keylessAxios.post(
      "/keyless/finish",
      undefined,
      keylessIdHeaders(token)
    )
  ).data;
}

export async function createSelfCheckoutUrl(token) {
  return (
    await keylessAxios.post(
      "/keyless/self-checkout",
      undefined,
      keylessIdHeaders(token)
    )
  ).data;
}

export async function confirmSelfCheckoutUrl(token) {
  return (
    await keylessAxios.post(
      "/keyless/confirm-self-checkout",
      undefined,
      keylessIdHeaders(token)
    )
  ).data;
}

export async function proceedNextSetp(token) {
  return (
    await keylessAxios.post(
      "/keyless/next-step",
      undefined,
      keylessIdHeaders(token)
    )
  ).data;
}

export async function getConventionDetails(token) {
  return (
    await keylessAxios.get(
      "/convention?token=" + token,
    )
  ).data;
}

export async function conventionOpenDoors(token) {
  return (
    await keylessAxios.post(
      "/convention/open-doors?token=" + token,
    )
  ).data;
}

function dataUrlToBlob(dataUrl) {
  const [header, base64] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)[1];
  const binary = atob(base64);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    array[i] = binary.charCodeAt(i);
  }
  return new Blob([array], {type: mime});
}

export async function getDamageReport(token) {
  return (await keylessAxios.get("/keyless/damage-report", keylessIdHeaders(token))).data;
}

export async function submitDamageReport(token, damageData) {
  const formData = new FormData();

  const parts = [];
  Object.entries(damageData).forEach(([part, data]) => {
    if (data.photos && data.photos.length > 0) {
      parts.push({part, notes: data.notes || ''});
      const safePartName = part.replace(/\s+/g, '_').replace(/[|]/g, '');
      // Só envia fotos novas (sem id — ainda não estão no backend)
      data.photos
        .filter((photo) => !photo.id)
        .forEach((photo, index) => {
          const blob = dataUrlToBlob(photo.dataUrl);
          formData.append(`${safePartName}_${index}`, blob, `${safePartName}_${index}.jpg`);
        });
    }
  });

  formData.append('damageData', JSON.stringify({parts}));

  return (await keylessAxios.post("/keyless/damage-report", formData, {
    headers: {
      "x-keyless-id": token,
      "Content-Type": "multipart/form-data",
    },
  })).data;
}

export async function deleteDamagePhoto(token, photoId) {
  return (await keylessAxios.delete(
    `/keyless/damage-report/photo/${photoId}`,
    keylessIdHeaders(token)
  )).data;
}

export async function getSelfcheckinPhotos(token) {
  return (await keylessAxios.get("/keyless/selfcheckin-photos", keylessIdHeaders(token))).data;
}

export async function submitSelfcheckinPhoto(token, photoDataUrl) {
  const formData = new FormData();
  const blob = dataUrlToBlob(photoDataUrl);
  formData.append('photo', blob, `selfcheckin_${Date.now()}.jpg`);

  return (await keylessAxios.post("/keyless/selfcheckin-photos", formData, {
    headers: {
      "x-keyless-id": token,
      "Content-Type": "multipart/form-data",
    },
  })).data;
}

export async function deleteSelfcheckinPhoto(token, photoId) {
  return (await keylessAxios.delete(
    `/keyless/selfcheckin-photos/${photoId}`,
    keylessIdHeaders(token)
  )).data;
}

export async function updateReturnStep(token, step) {
  return (await keylessAxios.post(
    "/keyless-return/next-step",
    { step },
    keylessIdHeaders(token)
  )).data;
}

export async function preCloseRA(token, systemFuel, clientFuel, systemKms, clientKms) {
  return (await keylessAxios.post(
    `/keyless/pre-close-ra`,
    {
      systemFuel: systemFuel,
      clientFuel: clientFuel,
      systemKms: systemKms,
      clientKms: clientKms
    },
    keylessIdHeaders(token),
  )).data
}
